"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { ArrowLeft, Key01 } from "@untitledui/icons"
import { useState } from "react"

import { BackgroundPattern } from "@/components/ui/background-pattern"
import { Button } from "@/components/ui/button"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { toFieldError } from "@/lib/utils"
import { identityPasswordResetPath, signInPath } from "@/routes"

// See sessions/new.tsx for detailed explanation of the Form Pattern.
// TL;DR: Uncontrolled inputs + FormData = copy-paste friendly UUI code.

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type PageProps = {
  errors: Record<string, string>
}

export default function ForgotPassword() {
  const { errors } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.post(
      identityPasswordResetPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <section className="bg-secondary_subtle min-h-screen overflow-hidden px-4 py-12 md:px-8 md:pt-24">
      <Head title="Forgot password" />
      <div className="sheet mx-auto flex w-full max-w-md flex-col gap-8 px-6 py-8 md:px-10 md:py-10">
        <div className="flex flex-col items-center gap-6 text-center">
          <div className="relative">
            <FeaturedIcon
              color="gray"
              theme="modern"
              size="xl"
              className="z-10"
            >
              <Key01 className="size-7" />
            </FeaturedIcon>
            <BackgroundPattern
              size="lg"
              pattern="grid"
              className="absolute top-1/2 left-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block"
            />
            <BackgroundPattern
              size="md"
              pattern="grid"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:hidden"
            />
          </div>

          <div className="z-10 flex flex-col gap-2 md:gap-3">
            <h1 className="text-display-xs md:text-display-sm text-primary">
              Forgot password?
            </h1>
            <p className="text-md text-tertiary self-stretch">
              No worries, we&apos;ll send you reset instructions.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="z-10 flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <Input
              label="Email"
              type="email"
              name="email"
              placeholder="Enter your email"
              size="md"
              isRequired
              hideRequiredIndicator
              isInvalid={!!errors?.email}
              error={toFieldError(errors?.email)}
              autoFocus
              autoComplete="email"
            />
          </div>

          <div className="flex flex-col gap-4">
            <Button
              type="submit"
              size="lg"
              isLoading={processing}
              showTextWhileLoading
            >
              Reset password
            </Button>
          </div>
        </form>

        <div className="z-10 flex justify-center gap-1 text-center">
          <Link
            href={signInPath()}
            className="text-tertiary hover:text-secondary flex items-center gap-2 text-sm font-semibold"
          >
            <ArrowLeft className="size-4" />
            Back to log in
          </Link>
        </div>
      </div>
    </section>
  )
}
